import { useCallback, useEffect, useMemo, useState } from "react";
import styled from 'styled-components';
import {
  Content,
  CONTENT_TYPES,
  Heading,
  HEADING_LEVELS,
  HEADING_TYPES, Loader
} from "cdk-radial";

import ApiExplorer from './ApiExplorer';
import AsyncApiExplorer from './AsyncApiExplorer';
import { API_TYPES } from '../api-directory-home/constants';
import DropdownMenu from '../common/DropdownMenu';

const BundleExplorerHeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

export default function BundleExplorer({
  bundleId,
  bundleApis,
  oneToOneApiAnon,
  downloadSpecFile,
  fetchApiSpec,
  fetchingAPISpecInBundle,
  errorInFetchingAPISpecInBundle,
  belongsTo,
  environmentURLs,
  auth
}) {
  const apiOptions = useMemo(
    () => bundleApis.map(api => ({ label: api.name, value: api.id })),
    [bundleApis]
  );

  const [lastFailedApiId, setLastFailedApiId] = useState(null);
  const [selectedApiOption, setSelectedApiOption] = useState(apiOptions[0]);
  const selectedApi = bundleApis.find(ba => ba.id === selectedApiOption?.value);

  const onApiSelected = useCallback(api => {
    setSelectedApiOption(apiOptions.find(o => o.value === api.value));
  }, [apiOptions]);

  useEffect(() => {
    if (
      selectedApi &&
      !selectedApi.environments &&
      !fetchingAPISpecInBundle &&
      selectedApi.id !== lastFailedApiId
    ) {
      fetchApiSpec?.({
        apiId: selectedApi.id,
        apiType: selectedApi.type,
        bundleId
      })
        .catch(() => {
          setLastFailedApiId(selectedApi.id);
        });
    }
  }, [
    bundleId,
    selectedApi,
    fetchApiSpec,
    fetchingAPISpecInBundle,
    lastFailedApiId
  ]);

  return (
    <>
      <BundleExplorerHeadingBlock>
        <Heading
          level={HEADING_LEVELS.LEVEL_2}
          headingType={HEADING_TYPES.HEADING_2}
        >
          API Specifications
        </Heading>
        <Content type={CONTENT_TYPES.BODY_2}>
          Select an API to view its specification details.
        </Content>
        <DropdownMenu
          options={apiOptions}
          value={selectedApiOption}
          onChange={onApiSelected}
          name="bundle-explorer-select-api"
          inputId="bundle-explorer-select-api"
          dataTestId="bundle-explorer-select-api"
          label="Select API"
          width="480px"
        />
      </BundleExplorerHeadingBlock>
      {selectedApi?.environments ? (
        selectedApi.type === API_TYPES.ASYNC ? (
          <AsyncApiExplorer
            key={selectedApi.id}
            bundleHeader={true}
            api={selectedApi}
            oneToOneApiAnon={oneToOneApiAnon}
            downloadSpec={downloadSpecFile}
            belongsTo={belongsTo}
            environmentURLs={environmentURLs}
          />
        ) : (
          <ApiExplorer
            key={selectedApi.id}
            bundleHeader={true}
            api={selectedApi}
            oneToOneApiAnon={oneToOneApiAnon}
            downloadSpec={downloadSpecFile}
            belongsTo={belongsTo}
            environmentURLs={environmentURLs}
            auth={auth}
          />
        )
      ) : errorInFetchingAPISpecInBundle ? (
        <Content type={CONTENT_TYPES.BODY_2}>
          Error while loading API Specifications.
        </Content>
      ) : (
        <Loader label="Loading API Specifications..." />
      )}
    </>
  );
}
